import { getAuthHeaders } from "./authSession";

export type SpotifyTokenRow = {
  accessToken: string;
  refreshToken?: string;
  tokenType?: string;
  scope?: string;
  expiresAt?: number | null;
  savedAt?: number;
};

export class SpotifyService {
  private async post<T>(payload: any): Promise<T> {
    const res = await fetch(process.env.REACT_APP_API_ENDPOINT!, {
      method: "POST",
      headers: { "content-type": "application/json", ...getAuthHeaders() },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `Request failed (${res.status})`);
    }

    return (await res.json()) as T;
  }

  getSpotifyToken() {
    return this.post<SpotifyTokenRow | null>({
      action: "getSpotifyToken",
    });
  }

  exchangeSpotifyAuthCode(payload: {
    code: string;
    clientId?: string;
    redirectUri: string;
    codeVerifier: string;
  }) {
    return this.post<{ ok: true; accessToken: string; expiresAt: number }>({
      action: "exchangeSpotifyAuthCode",
      ...payload,
    });
  }

  refreshSpotifyToken(payload?: { clientId?: string }) {
    return this.post<{ ok: true; accessToken: string; expiresAt: number }>({
      action: "refreshSpotifyToken",
      ...payload,
    });
  }

  deleteSpotifyToken() {
    return this.post<{ ok: true }>({
      action: "deleteSpotifyToken",
    });
  }

  /**
   * Gets a valid access token, automatically refreshing if expired.
   * Returns null if no token exists or refresh fails.
   */
  async getValidAccessToken(clientId?: string): Promise<string | null> {
    try {
      const tokenData = await this.getSpotifyToken();

      if (!tokenData?.accessToken) {
        return null;
      }

      const now = Date.now();
      const expiresAt = tokenData.expiresAt ?? 0;
      const bufferTime = 60 * 1000;

      if (expiresAt > now + bufferTime) {
        return tokenData.accessToken;
      }

      if (!tokenData.refreshToken) {
        return null;
      }

      const refreshResult = await this.refreshSpotifyToken({ clientId });
      return refreshResult.accessToken;
    } catch (e) {
      console.error("Failed to get valid Spotify access token:", e);
      return null;
    }
  }
}
